import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { auth, provider, signInWithPopup } from '../services/firebase';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { showToast } from '../components/UI';

const AGENTS = [
  { label: 'CEO Agent',       desc: 'Reviews and plans your work every night',           color: '#e8a87c' },
  { label: 'Content Agent',   desc: 'Writes copy, blogs, and messaging',                  color: '#5dcaa5' },
  { label: 'Marketing Agent', desc: 'Prepares customer profile, strategy, and campaigns', color: '#c8563a' },
  { label: 'Builder Agent',   desc: 'Builds and deploys your Branding Experience',                    color: '#378add' },
  { label: 'Ops Agent',       desc: 'Prepares processes, timelines, and operations',      color: '#e8a87c' },
];

export default function AuthPage() {
  const { registerBackend } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(false);
const isAuthInProcess = React.useRef(false);

  const refCode = params.get('ref');
  useEffect(() => {
    if (refCode) sessionStorage.setItem('raji_ref', refCode);
  }, [refCode]);


async function handleGoogleAuth() {
  // 2. Check the synchronous lock immediately
  if (isAuthInProcess.current) return;

  try {
    isAuthInProcess.current = true; // Lock it
    setLoading(true);

    const result = await signInWithPopup(auth, provider);

    const storedRef = sessionStorage.getItem('raji_ref') || undefined;
    await registerBackend(result.user, storedRef);

    if (storedRef) sessionStorage.removeItem('raji_ref');

    const ws = await api.getWorkspaces();
    navigate(ws?.length > 0 ? '/dashboard/ceo-runlog' : '/onboarding', { replace: true });

  } catch (err) {
    // Check if it's a real error or just a user cancellation
    if (err?.code !== 'auth/popup-closed-by-user' && err?.code !== 'auth/cancelled-popup-request') {
      showToast('Google sign-in failed. Please try again.', 'error');
    }
    console.error(err);
  } finally {
    // 3. Release the lock and the loading state
    setLoading(false);
    isAuthInProcess.current = false;
  }
}

  function planLabel() {
    const plan = params.get('plan');
    if (plan === 'growth') return 'Growth plan · $49/mo';
    if (plan === 'pro')    return 'Pro plan · $99/mo';
    if (plan === 'start')  return 'Starter plan · $24/mo';
    if (refCode || sessionStorage.getItem('raji_ref'))
      return 'Referral bonus · +2,000 points on signup';
    return 'Free trial · No credit card needed';
  }

  const isReferral = !!(refCode || sessionStorage.getItem('raji_ref'));

  return (
    <>
      <style>{`
        .ap-root *,
        .ap-root *::before,
        .ap-root *::after { box-sizing: border-box; margin: 0; padding: 0; }
        .ap-root {
          font-family: var(--sans, 'DM Sans', sans-serif);
          background: var(--bg, #f5f2eb);
          color: var(--ink, #1a1a18);
          min-height: 100vh;
          display: flex;
          flex-direction: column;
        }
        .ap-topbar { padding: 18px 20px; background: var(--ink, #1a1a18); }
        .ap-brand { font-family: var(--serif, Georgia, serif); font-size: 22px; color: #f5f2eb; text-decoration: none; letter-spacing: -0.02em; }
        .ap-brand span { color: #e8a87c; }
        .ap-hero { background: var(--ink, #1a1a18); color: #f5f2eb; padding: 28px 20px 32px; position: relative; overflow: hidden; flex-shrink: 0; }
        .ap-hero::after { content: ''; position: absolute; inset: 0; pointer-events: none; background-image: linear-gradient(rgba(245,242,235,.04) 1px, transparent 1px), linear-gradient(90deg, rgba(245,242,235,.04) 1px, transparent 1px); background-size: 44px 44px; }
        .ap-hero-inner { position: relative; z-index: 1; }
        .ap-headline { font-family: var(--serif, Georgia, serif); font-size: clamp(28px, 8vw, 48px); line-height: 1.04; letter-spacing: -0.03em; margin-bottom: 12px; }
        .ap-headline em { color: #e8a87c; font-style: italic; }
        .ap-subtext { font-size: 14px; line-height: 1.65; color: rgba(245,242,235,.68); max-width: 520px; margin-bottom: 24px; }
        .ap-agents { display: flex; flex-direction: column; gap: 8px; }
        .ap-agent { display: flex; align-items: center; gap: 12px; padding: 11px 13px; border-radius: 10px; background: rgba(245,242,235,.06); border: 1px solid rgba(245,242,235,.1); }
        .ap-agent-dot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }
        .ap-agent-label { font-size: 13px; font-weight: 600; color: #f5f2eb; line-height: 1.3; }
        .ap-agent-desc { font-size: 11px; line-height: 1.45; color: rgba(245,242,235,.5); }
        .ap-card-section { flex: 1; padding: 32px 20px 40px; background: var(--bg, #f5f2eb); }
        .ap-card { width: 100%; max-width: 440px; margin: 0 auto; }
        .ap-eyebrow { font-family: var(--serif, Georgia, serif); font-size: clamp(28px, 8vw, 38px); line-height: 1; letter-spacing: -0.03em; color: #e0187c; margin-bottom: 8px; }
        .ap-title { font-family: var(--serif, Georgia, serif); font-size: clamp(22px, 6vw, 30px); line-height: 1.08; letter-spacing: -0.03em; color: var(--ink, #1a1a18); margin-bottom: 8px; }
        .ap-text { font-size: 14px; color: var(--ink-soft, #5a5a52); margin-bottom: 20px; line-height: 1.55; }
        .ap-pill { display: inline-flex; align-items: center; gap: 5px; padding: 5px 11px; border-radius: 999px; background: rgba(15,110,86,.09); color: var(--green, #0f6e56); font-size: 12px; font-weight: 500; margin-bottom: 22px; }
        .ap-pill-referral { background: rgba(232,168,124,0.15); color: #9a6030; }
        .ap-btn { width: 100%; min-height: 52px; display: flex; align-items: center; justify-content: center; gap: 10px; background: #fff; color: var(--ink, #1a1a18); border: 1px solid rgba(0,0,0,.12); border-radius: 12px; font-family: var(--sans, 'DM Sans', sans-serif); font-size: 15px; font-weight: 600; cursor: pointer; transition: box-shadow .15s ease, transform .1s ease; -webkit-tap-highlight-color: transparent; }
        .ap-btn:hover:not(:disabled) { box-shadow: 0 4px 18px rgba(0,0,0,.1); transform: translateY(-1px); }
        .ap-btn:active:not(:disabled) { transform: translateY(0); }
        .ap-btn:disabled { opacity: .7; cursor: not-allowed; }
        .ap-spinner { width: 18px; height: 18px; border-radius: 50%; border: 2.5px solid rgba(0,0,0,.15); border-top-color: var(--ink, #1a1a18); animation: ap-spin .7s linear infinite; flex-shrink: 0; }
        @keyframes ap-spin { to { transform: rotate(360deg); } }
        .ap-legal { font-size: 12px; line-height: 1.6; text-align: center; color: var(--ink-mute, #9a9a90); margin-top: 16px; }
        .ap-legal a { color: var(--ink-soft, #5a5a52); text-decoration: underline; text-underline-offset: 2px; }
        .ap-footer { font-size: 11px; color: rgba(245,242,235,.25); margin-top: 28px; }
        @media (min-width: 900px) {
          .ap-root { flex-direction: row; height: 100vh; overflow: hidden; }
          .ap-left { flex: 1.05; display: flex; flex-direction: column; background: var(--ink, #1a1a18); overflow-y: auto; }
          .ap-topbar { padding: 24px 36px; }
          .ap-hero { flex: 1; padding: 32px 36px 40px; }
          .ap-right { flex: 0.95; min-width: 400px; max-width: 560px; overflow-y: auto; display: flex; align-items: center; justify-content: center; padding: 40px 36px; background: var(--bg, #f5f2eb); }
          .ap-card-section { display: none; }
          .ap-right .ap-card { width: 100%; max-width: 400px; }
        }
        @media (max-width: 899px) {
          .ap-left { display: contents; }
          .ap-right { display: none; }
        }
      `}</style>

      <div className="ap-root">
        <div className="ap-left">
          <div className="ap-topbar">
            <Link to="/" className="ap-brand">raji<span>.</span>ai</Link>
          </div>
          <section className="ap-hero">
            <div className="ap-hero-inner">
              <h2 className="ap-headline">
                AI agents build your business,<br /><em> while you sleep</em>
              </h2>
              <p className="ap-subtext">
                Strategy, content, campaigns created overnight automatically.
              </p>
              <div className="ap-agents">
                {AGENTS.map((agent) => (
                  <div key={agent.label} className="ap-agent">
                    <span className="ap-agent-dot" style={{ background: agent.color }} />
                    <div>
                      <div className="ap-agent-label">{agent.label}</div>
                      <div className="ap-agent-desc">{agent.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="ap-footer">© 2026 Raji AI</div>
            </div>
          </section>
        </div>

        <div className="ap-right">
          <AuthCard loading={loading} planLabel={planLabel} handleGoogleAuth={handleGoogleAuth} isReferral={isReferral} />
        </div>
        <section className="ap-card-section">
          <AuthCard loading={loading} planLabel={planLabel} handleGoogleAuth={handleGoogleAuth} isReferral={isReferral} />
        </section>
      </div>
    </>
  );
}

function AuthCard({ loading, planLabel, handleGoogleAuth, isReferral }) {
  return (
    <div className="ap-card">
      <div className="ap-eyebrow">Start your first build</div>
      <div className="ap-title">Get started free</div>
      <p className="ap-text">
        {isReferral
          ? 'A friend invited you — you both get bonus points.'
          : 'Your AI business team is ready to go.'}
      </p>
      <div className={`ap-pill${isReferral ? ' ap-pill-referral' : ''}`}>
        {isReferral ? '🎁' : '✓'} {planLabel()}
      </div>
      <button type="button" className="ap-btn" onClick={handleGoogleAuth} disabled={loading}>
        {loading ? <span className="ap-spinner" /> : <GoogleIcon />}
        <span>{loading ? 'Signing in…' : 'Continue with Google'}</span>
      </button>
      <p className="ap-legal">
        By continuing you agree to Raji&apos;s{' '}
        <Link to="/terms">Terms</Link> and{' '}
        <Link to="/privacy">Privacy Policy</Link>.
      </p>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.909-2.258c-.806.54-1.837.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.712A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.712V4.956H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.044l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.956L3.964 7.288C4.672 5.163 6.656 3.58 9 3.58z"/>
    </svg>
  );
}